import {
  IPushDailyStat,
  IPushFilters,
  IPushStatsApiKeyOption,
} from '../../types/pushStats';

export const MAX_RANGE_DAYS = 366;
const DAY_MS = 86400000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const defaultFilters: IPushFilters = {
  source: 'all',
  apiKeyId: '',
  notificationType: '',
  couponId: '',
};

// All dates are YYYY-MM-DD strings; arithmetic is done in UTC so it never
// drifts with the browser's time zone or DST.
export const parseDate = (date: string): Date => new Date(`${date}T00:00:00Z`);
export const formatDate = (date: Date): string => date.toISOString().slice(0, 10);
export const addDays = (date: string, days: number): string =>
  formatDate(new Date(parseDate(date).getTime() + days * DAY_MS));
export const daysBetween = (from: string, to: string): number =>
  Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / DAY_MS) + 1;

// The backend buckets days in Europe/London, so "today" must match.
export const todayInLondon = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());

export const weekdayShort = (date: string): string => WEEKDAYS[parseDate(date).getUTCDay()];

export const formatLongDate = (date: string): string =>
  parseDate(date).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const formatShortDate = (date: string): string =>
  parseDate(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** The range of equal length immediately before from..to. */
export const previousRange = (from: string, to: string) => {
  const length = daysBetween(from, to);
  return { from: addDays(from, -length), to: addDays(from, -1) };
};

export const totalOf = (row: IPushDailyStat): number => row.customSent + row.couponSent;

export interface IPushTotals {
  custom: number;
  coupon: number;
  failed: number;
  noRecipients: number;
  total: number;
  /** Custom push only (coupon pushes record no failures); null when nothing was sent. */
  successRate: number | null;
}

export const sumStats = (rows: IPushDailyStat[]): IPushTotals => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.custom += row.customSent;
      acc.coupon += row.couponSent;
      acc.failed += row.customFailed;
      acc.noRecipients += row.customNoRecipients;
      return acc;
    },
    { custom: 0, coupon: 0, failed: 0, noRecipients: 0 }
  );
  return {
    ...totals,
    total: totals.custom + totals.coupon,
    successRate: totals.custom ? ((totals.custom - totals.failed) / totals.custom) * 100 : null,
  };
};

/** Percent change from prev to cur; null when there is no baseline. */
export const percentChange = (cur: number, prev: number): number | null =>
  prev === 0 ? null : ((cur - prev) / prev) * 100;

export const peakDay = (rows: IPushDailyStat[]): IPushDailyStat | null =>
  rows.reduce<IPushDailyStat | null>(
    (best, row) => (best === null || totalOf(row) > totalOf(best) ? row : best),
    null
  );

export type SortKey = 'date' | 'customSent' | 'customFailed' | 'customNoRecipients' | 'couponSent' | 'total';

export const sortValue = (row: IPushDailyStat, key: SortKey): string | number =>
  key === 'total' ? totalOf(row) : key === 'date' ? row.date : row[key];

export const sortRows = (rows: IPushDailyStat[], key: SortKey, direction: 1 | -1): IPushDailyStat[] =>
  rows.slice().sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    const cmp = x < y ? -1 : x > y ? 1 : 0;
    return cmp * direction || (a.date < b.date ? 1 : -1);
  });

/**
 * Applies a filter change and keeps the set consistent: API key / type only
 * exist on custom pushes, coupons only on coupon pushes, so choosing one
 * clears and excludes the other side.
 */
export const applyFilterChange = (
  current: IPushFilters,
  change: Partial<IPushFilters>,
  apiKeys: IPushStatsApiKeyOption[]
): IPushFilters => {
  const next = { ...current, ...change };

  if (change.source === 'coupon') {
    next.apiKeyId = '';
    next.notificationType = '';
  }
  if (change.source === 'custom') {
    next.couponId = '';
  }
  if (change.apiKeyId || change.notificationType) {
    next.couponId = '';
  }
  if (change.couponId) {
    next.apiKeyId = '';
    next.notificationType = '';
  }
  if ((next.apiKeyId || next.notificationType) && next.source === 'coupon') {
    next.source = 'custom';
  }
  if (next.couponId && next.source === 'custom') {
    next.source = 'coupon';
  }

  const key = apiKeys.find((k) => k.apiKeyId === next.apiKeyId);
  if (key && next.notificationType && !key.notificationTypes.includes(next.notificationType)) {
    next.notificationType = '';
  }
  return next;
};

export const filtersAreDefault = (filters: IPushFilters): boolean =>
  filters.source === 'all' && !filters.apiKeyId && !filters.notificationType && !filters.couponId;

export const buildCsv = (rows: IPushDailyStat[], filterSummary: string): string => {
  const header =
    'date,weekday,custom_sent,custom_failed,custom_no_recipients,coupon_sent_first_per_customer,total_sent';
  const lines = rows.map((row) =>
    [
      row.date,
      weekdayShort(row.date),
      row.customSent,
      row.customFailed,
      row.customNoRecipients,
      row.couponSent,
      totalOf(row),
    ].join(',')
  );
  return [
    header,
    ...lines,
    '',
    `# filters: ${filterSummary}`,
    '# coupon_sent counts each customer once (first send only); coupon pushes have no failure data. total_sent = custom_sent + coupon_sent.',
    '',
  ].join('\n');
};
